// features/contracts/hooks/useApprovalRequestFlow.ts
import { createApproversNotification } from 'components/Notifications/createNotifyPayload'
import { useCallback } from 'react'
import { useRequestApproval } from './useRequestApproval'

type ApproverLike = { id: string; name?: string; image?: string }
type ActiveUser = {
  id?: string
  name?: string
  email?: string
  image_512?: string
  user?: { id?: string; real_name?: string; profile?: { email?: string; image_512?: string } }
} | null
type DealLike = { PK: string; SK: string; deal_uuid: string } | undefined

export function useApprovalRequestFlow() {
  const { requestApproval, isRequesting } = useRequestApproval()

  const pick = <T extends unknown>(...vals: T[]) =>
    vals.find(v => v !== undefined && v !== null && v !== '') as T | undefined

  const buildRequestor = (activeUser: any) => {
    const id =
      pick(
        activeUser?.user?.id,
        activeUser?.id,
        activeUser?.user_uuid, // ← common in your app
        activeUser?.sub
      ) ?? 'unknown'

    const name =
      pick(
        activeUser?.user?.real_name,
        activeUser?.name,
        [activeUser?.firstName, activeUser?.lastName].filter(Boolean).join(' ')
      ) ?? '' // ← avoid undefined so it won't be stripped

    const email = pick(activeUser?.user?.profile?.email, activeUser?.email) ?? ''

    const image = pick(activeUser?.user?.profile?.image_512, activeUser?.image_512) ?? ''

    return { id, name, email, image, hasRequested: true }
  }

  const handleApprovalRequest = useCallback(
    async (args: {
      deal: DealLike
      ok: boolean
      productCount: number
      isDirty: boolean
      productsDirty: boolean
      approverObjects: ApproverLike[]
      activeUser: ActiveUser
      onSaveAll: () => Promise<any>
      showSnackbar: (_msg: string, _variant?: 'success' | 'error' | 'warning' | 'info') => void
    }) => {
      const {
        deal,
        ok,
        productCount,
        isDirty,
        productsDirty,
        approverObjects,
        activeUser,
        onSaveAll,
        showSnackbar,
      } = args

      if (!deal) return

      if (!ok || productCount <= 0) {
        showSnackbar('Add at least one service and fix validation errors first.', 'warning')
        return
      }

      // Validate approvers exist before proceeding
      if (!approverObjects || approverObjects.length === 0) {
        showSnackbar('No approvers found for this deal. Please contact an administrator.', 'error')
        return
      }

      try {
        // Save any pending edits so backend sees the latest
        if (isDirty || productsDirty) {
          await onSaveAll()
        }

        const approvers = (approverObjects ?? [])
          .map((a: any) => {
            const id = a.id ?? a.user_uuid ?? a.contact_id ?? a.panda_contact_id ?? a.email ?? ''

            const name = a.name ?? [a.firstName, a.lastName].filter(Boolean).join(' ') ?? ''

            const image = a.image ?? a.image_512 ?? ''

            return {
              id,
              name,
              image,
              hasApproved: false,
              hasRejected: false,
            }
          })
          .filter(a => a.id)

        // Double-check after mapping that we have valid approvers with IDs
        if (approvers.length === 0) {
          showSnackbar('Could not resolve approvers. Please try again or contact an administrator.', 'error')
          return
        }

        console.log('approvers', approvers)
        const notificationPayload =
          approvers?.length > 0
            ? createApproversNotification(
                deal,
                activeUser?.user?.id ?? activeUser?.id ?? (activeUser as any)?.user_uuid ?? 'unknown',
                approvers
              )
            : []
        console.log('notificationPayload')
        const requestedBy = activeUser?.user?.id ?? activeUser?.id ?? (activeUser as any)?.user_uuid ?? 'unknown'

        console.log('requestedBy', requestedBy)
        const requestor = activeUser ? buildRequestor(activeUser) : null
        console.log('requestor', requestor)
        console.log({
          dealPK: deal.PK,
          dealSK: deal.SK,
          deal_uuid: deal.deal_uuid,
          approvers,
          requestedBy,
          requestor,
          notificationPayload,
        })
        await requestApproval({
          dealPK: deal.PK,
          dealSK: deal.SK,
          deal_uuid: deal.deal_uuid,
          approvers,
          requestedBy,
          requestor,
          notificationPayload,
        })

        // showSnackbar('Approval requested.', 'success')
      } catch (e) {
        console.error(e)
        showSnackbar('Failed to request approval.', 'error')
      }
    },
    [requestApproval]
  )

  return { handleApprovalRequest, isRequesting }
}
